import { Head, Link, router, usePage } from '@inertiajs/react'
import { Anchor, Badge, Button, Card, Container, Group, NativeSelect, Paper, SimpleGrid, Stack, Text, Title, UnstyledButton } from '@mantine/core'
import { useState } from 'react'
import EmptyState from '@/components/EmptyState'
import { ProjectTypeBadge, SessionStatusBadge } from '@/components/badges'
import { submitHiddenForm } from '@/lib/formSubmission'
import { timeAgo, statusLabel } from '@/lib/session-utils'
import type { AgentProps, DashboardPageProps, DashboardSessionProps, ProjectProps } from '@/types'

function NewSessionButton({ agents, projectId, csrfToken }: { agents: AgentProps[]; projectId: string; csrfToken: string }) {
  const [selectedAgentId, setSelectedAgentId] = useState(agents[0]?.id || '')

  if (agents.length === 0) return null

  return (
    <Group align="flex-end">
      {agents.length > 1 ? (
        <NativeSelect
          label="Agent"
          value={selectedAgentId}
          onChange={(e) => setSelectedAgentId(e.target.value)}
          data={agents.map((agent) => ({ value: agent.id, label: agent.name }))}
        />
      ) : null}
      <Button
        onClick={() => submitHiddenForm({
          action: `/agents/${selectedAgentId}/sessions`,
          values: {
            authenticity_token: csrfToken,
            project_id: projectId,
          },
        })}
      >
        + New Chat
      </Button>
    </Group>
  )
}

function ProjectCard({ project, agents, csrfToken }: { project: ProjectProps; agents: AgentProps[]; csrfToken: string }) {
  return (
    <Card>
      <Stack gap="md">
        <Group justify="space-between" align="flex-start">
          <div>
            <Anchor component={Link} href={`/projects/${project.id}`} fw={600}>
              {project.name}
            </Anchor>
            {project.description ? <Text c="dimmed" size="sm" mt={4}>{project.description}</Text> : null}
          </div>
          <ProjectTypeBadge type={project.project_type} />
        </Group>

        <NewSessionButton agents={agents} projectId={project.id} csrfToken={csrfToken} />
      </Stack>
    </Card>
  )
}

function AgentCard({ agent }: { agent: AgentProps }) {
  return (
    <Card>
      <Stack gap="md">
        <Group justify="space-between" align="flex-start">
          <div>
            <Text fw={600}>{agent.name}</Text>
            {agent.description ? <Text c="dimmed" size="sm" mt={4}>{agent.description}</Text> : null}
          </div>
          <Badge variant="light">{agent.role}</Badge>
        </Group>

        <Button component={Link} href={`/agents/${agent.id}`} variant="light">
          Open agent
        </Button>
      </Stack>
    </Card>
  )
}

function SessionRow({ session }: { session: DashboardSessionProps }) {
  const label = statusLabel(session.status)

  return (
    <UnstyledButton onClick={() => router.visit(`/projects/${session.project_id}/chats?session=${session.id}`)}>
      <Paper p="md" withBorder>
        <Group justify="space-between" align="flex-start">
          <Stack gap={6} style={{ flex: 1 }}>
            <Text fw={500}>{session.title || session.preview || 'New conversation'}</Text>
            <Text c="dimmed" size="sm">
              {session.agent_name}{session.project_name ? ` - ${session.project_name}` : ''}
            </Text>
          </Stack>

          <Stack gap={6} align="flex-end">
            {label ? <SessionStatusBadge status={session.status} /> : null}
            <Text c="dimmed" size="xs">
              {session.message_count > 0 ? `${session.message_count} msgs - ` : ''}
              {timeAgo(session.last_activity_at || session.started_at)}
            </Text>
          </Stack>
        </Group>
      </Paper>
    </UnstyledButton>
  )
}

export default function Dashboard() {
  const { agents, dashboard_projects: rawProjects, csrf_token, latest_sessions, organization } = usePage<DashboardPageProps>().props
  const orgPrefix = organization ? `/orgs/${organization.slug}` : ''
  const projects = [...rawProjects].sort((a, b) => {
    if (a.project_type === 'global') return -1
    if (b.project_type === 'global') return 1
    return 0
  })

  return (
    <>
      <Head title="Dashboard" />

      <Container size="xl" py="md">
        <Stack gap="xl">
          <Title order={2}>Dashboard</Title>

          <div>
            <Title order={3} mb="md">Agents</Title>
            {agents.length > 0 ? (
              <SimpleGrid cols={{ base: 1, md: 2, xl: 3 }}>
                {agents.map((agent) => (
                  <AgentCard key={agent.id} agent={agent} />
                ))}
              </SimpleGrid>
            ) : (
              <EmptyState>No agents yet. They will be created automatically.</EmptyState>
            )}
          </div>

          {projects.length > 0 ? (
            <div>
              <Group justify="space-between" align="center" mb="md">
                <Title order={3}>Projects</Title>
                <Anchor component={Link} href={`${orgPrefix}/projects`}>Browse all</Anchor>
              </Group>

              <SimpleGrid cols={{ base: 1, md: 2, xl: 3 }}>
                {projects.map((project) => (
                  <ProjectCard key={project.id} project={project} agents={agents} csrfToken={csrf_token as string} />
                ))}
              </SimpleGrid>
            </div>
          ) : null}

          {latest_sessions.length > 0 ? (
            <div>
              <Title order={3} mb="md">Latest sessions</Title>
              <Stack gap="sm">
                {latest_sessions.map((session) => (
                  <SessionRow key={session.id} session={session} />
                ))}
              </Stack>
            </div>
          ) : null}
        </Stack>
      </Container>
    </>
  )
}
